import { AcumaticaRequestOptions, AcumaticaService } from '../acumatica.service';
import { AcumaticaEntity, AcumaticaEntityService } from './acumatica-entity';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Acumatica, AcumaticaFieldFormat } from '../acumatica-field-decorator';
import * as errors from '../acumatica-errors';
import { AcumaticaLoadingOptions } from '../loading-indicator/AcumaticaLoadingOptions';
import { AppConfig } from 'src/app/app-config.service';

const acceptAdjustmentsLessThanYardsDefault = 5;
const acceptAdjustmentsWithinNPercent = 0.1;

@Injectable({
    providedIn: 'root'
})

export class AdjustmentService extends AcumaticaEntityService {
    constructor(
        protected acumatica: AcumaticaService
    ) {
        super(acumatica, 'InventoryAdjustment', Adjustment);
    }

    get(
        ReferenceNbr: string,
        options: AcumaticaRequestOptions = {}
    ): Observable<Adjustment> {
        return new Observable<Adjustment>(observer => {
            // Get the Adjustment Document
            return super.getEntity(ReferenceNbr, {
                ...{ loader: { message: "Getting Adjustment" } },
                ...options
            }).subscribe(observer);
        })
    }

    put(
        adjustment: Adjustment,
        options: AcumaticaRequestOptions = {}
    ): Observable<Adjustment> {
        return new Observable<Adjustment>(observer => {
            return super.putEntity(adjustment, {
                ...{ loader: { message: "Creating Adjustment" } },
                ...options
            }).subscribe(observer);
        })
    }

    Release(
        adjustment: Adjustment,
        options: AcumaticaLoadingOptions | null = {}
    ): Observable<void> {
        return new Observable<void>(observer => {
            return super.action(adjustment, 'ReleaseAdjustment', {
                ...{ message: "Releasing Adjustment" },
                ...options
            }, options).subscribe(observer);
        })
    }
}

export class AdjustmentDetail extends AcumaticaEntity {
    public acceptAdjustmentsLessThanYards = acceptAdjustmentsLessThanYardsDefault;

    @Acumatica() InventoryID?: string;
    @Acumatica() LocationID: string | null = null;
    @Acumatica() LotSerialNbr: string | null = null;
    @Acumatica('Qty') Quantity: number = 0;
    @Acumatica() ReasonCode: string | null = AppConfig.settings.acumatica.PickingPhysicalAdjustmentReasonCode;
    @Acumatica() WarehouseID: string | null = null;
    @Acumatica() Description: string | null = null;
    @Acumatica() UOM: string | null = null;
    @Acumatica({
        writer: (value: boolean | null) => (value === null) ? '' : (value ? 'Match' : 'Non-Match')
    }) CommercialMatch: boolean | null = null;

    private _AdjustQtyTo: number | null = null;
    get AdjustQtyTo() { return this._AdjustQtyTo; }
    set AdjustQtyTo(value: number | null) {
        if (value?.toString() == '') value = null;
        this._AdjustQtyTo = value;
    }

    constructor(init?: any) {
        super();
        Object.assign(this, init);
    }

    OriginalQuantity?: number;
    /** when set to true .AdjustmentAcceptable will always return true */
    BypassAdjustmentAcceptableValidation: boolean = false;
    /** returns true if the adjustment quantity is within acceptable threshold (less than 5 yards or 10% of roll balance) */
    public get AdjustmentAcceptable(): boolean {
        if (this.BypassAdjustmentAcceptableValidation == true) return true;
        if (this.OriginalQuantity === undefined) throw new Error("Use of AdjustmentAcceptable() requires originalQuantity be set");

        /*** works but can be written more legibly
        // let adjustmentAmt = this.AdjustQtyTo === null ? this.Quantity : (this.AdjustQtyTo - this.OriginalQuantity);
        // if (Math.abs(adjustmentAmt) <= this.acceptAdjustmentsLessThanYards) return true;

        // let newQuantity = this.OriginalQuantity + adjustmentAmt;
        // return newQuantity > this.OriginalQuantity * (1 - acceptAdjustmentsWithinNPercent)
        //     && newQuantity < this.OriginalQuantity * (1 + acceptAdjustmentsWithinNPercent);
        ***/

        // adjustment must be less than `acceptAdjustmentsLessThanYards` and `acceptAdjustmentsWithinNPercent` % of the original quantity
        let maxVariance = Math.max(
            this.acceptAdjustmentsLessThanYards,
            this.OriginalQuantity * (1 - acceptAdjustmentsWithinNPercent));
        let variance = Math.abs(this.AdjustQtyTo === null ? this.Quantity : (this.AdjustQtyTo - this.OriginalQuantity));
        // console.warn('variance', variance, 'maxVariance', maxVariance);
        return variance < maxVariance;
    }
}

export class Adjustment extends AcumaticaEntity {
    @Acumatica() note!: string;
    @Acumatica() ReferenceNbr!: string;
    @Acumatica() ExternalRef?: string;
    @Acumatica() Status!: string;
    @Acumatica() Description?: string;
    @Acumatica({ format: AcumaticaFieldFormat.Date }) Date!: Date;

    @Acumatica(AdjustmentDetail) Details: AdjustmentDetail[] = [];

    constructor(init?: any) {
        super();
        Object.assign(this, init);
    }
}